import { useEffect } from 'react';
import type { ResolvedSeo } from '@cng/shared';

/**
 * Head tags are emitted as a string during SSR (so crawlers see them in the
 * initial HTML) and reconciled in the DOM on client-side navigation. Both paths
 * build from the same `ResolvedSeo` the server produced.
 */

function escapeAttr(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * A <style> element ends at the first `</style`, whatever is around it, so that
 * sequence is what must not survive. The values themselves are already
 * restricted to hex and rgb() by `isSafeColour`; this is the second line.
 */
function escapeStyle(css: string): string {
  return css.replace(new RegExp('</(style)', 'gi'), '<\\/$1');
}

function escapeJsonLd(value: unknown): string {
  // Only "<" can terminate the script element early.
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

export interface HeadExtras {
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  faviconUrl?: string;
  siteName?: string;
  /**
   * The image the page paints largest — its LCP element. Preloading it is worth
   * a lot: measured on a phone over slow 4G, the homepage hero began at 263ms
   * but did not finish until 5.3s, because it queued behind the stylesheet, the
   * JavaScript, six font files and the tag manager on a 1.6 Mbps link.
   *
   * `srcset` and `sizes` must be carried across when the image sits inside a
   * `<picture>`: the browser picks a WebP derivative from the source set, so a
   * preload naming only the fallback would fetch a file it never displays.
   */
  lcpImage?: { href: string; srcset?: string; sizes?: string };
  /** The resolved palette, already serialised as a CSS rule. */
  themeCss?: string;
}

/** Server-side: render the complete <head> content for a page. */
export function renderHeadTags(seo: ResolvedSeo, extras: HeadExtras = {}): string {
  const tags: string[] = [];
  const meta = (attr: 'name' | 'property', key: string, content: string) => {
    if (!content) return;
    tags.push(`<meta data-cng-head ${attr}="${escapeAttr(key)}" content="${escapeAttr(content)}">`);
  };

  tags.push(`<title>${escapeAttr(seo.title)}</title>`);
  meta('name', 'description', seo.description);
  meta('name', 'robots', seo.robots);
  if (seo.canonical) tags.push(`<link data-cng-head rel="canonical" href="${escapeAttr(seo.canonical)}">`);

  meta('property', 'og:title', seo.ogTitle);
  meta('property', 'og:description', seo.ogDescription);
  meta('property', 'og:type', seo.ogType);
  meta('property', 'og:url', seo.canonical);
  meta('property', 'og:image', seo.ogImage);
  meta('property', 'og:locale', 'en_IN');
  if (extras.siteName) meta('property', 'og:site_name', extras.siteName);

  meta('name', 'twitter:card', seo.twitterCard);
  meta('name', 'twitter:title', seo.ogTitle);
  meta('name', 'twitter:description', seo.ogDescription);
  meta('name', 'twitter:image', seo.ogImage);

  if (extras.faviconUrl) {
    tags.push(`<link rel="icon" href="${escapeAttr(extras.faviconUrl)}">`);
    tags.push(`<link rel="apple-touch-icon" href="${escapeAttr(extras.faviconUrl)}">`);
  }
  tags.push('<link rel="manifest" href="/site.webmanifest">');

  if (extras.themeCss) {
    // In the first response, so the page is never painted in the default
    // palette and then swapped. `data-cng-theme` lets the admin's live preview
    // find and replace it without touching anything else.
    tags.push(`<style data-cng-theme>${escapeStyle(extras.themeCss)}</style>`);
  }

  if (extras.lcpImage?.href) {
    const { href, srcset, sizes } = extras.lcpImage;
    tags.push(
      '<link rel="preload" as="image" fetchpriority="high"'
      + ` href="${escapeAttr(href)}"`
      + (srcset ? ` imagesrcset="${escapeAttr(srcset)}"` : '')
      + (sizes ? ` imagesizes="${escapeAttr(sizes)}"` : '')
      + '>',
    );
  }

  for (const block of seo.jsonLd ?? []) {
    tags.push(`<script data-cng-head type="application/ld+json">${escapeJsonLd(block)}</script>`);
  }

  if (extras.googleTagManagerId) {
    // Injected after `load` rather than during it. The tag manager pulls several
    // scripts from three Google domains; started immediately it competes with
    // the hero image for a phone's bandwidth and delays the largest paint. No
    // visit is lost by measuring it a second later.
    tags.push(
      `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`
      + `function load(){var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';`
      + `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);}`
      + `if(d.readyState==='complete'){setTimeout(load,0);}else{w.addEventListener('load',function(){setTimeout(load,0);});}})`
      + `(window,document,'script','dataLayer','${escapeAttr(extras.googleTagManagerId)}');</script>`,
    );
  } else if (extras.googleAnalyticsId) {
    tags.push(
      `<script async src="https://www.googletagmanager.com/gtag/js?id=${escapeAttr(extras.googleAnalyticsId)}"></script>`,
      `<script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}`
      + `gtag('js',new Date());gtag('config','${escapeAttr(extras.googleAnalyticsId)}');</script>`,
    );
  }

  return tags.join('\n    ');
}

const MANAGED = 'data-cng-head';

/** Client-side: keep the document head in sync as the visitor navigates. */
export function useHead(seo: ResolvedSeo | null | undefined, extras: HeadExtras = {}): void {
  useEffect(() => {
    if (!seo || typeof document === 'undefined') return;

    document.title = seo.title;

    // Replace only what we own; never touch tags the shell shipped.
    document.querySelectorAll(`[${MANAGED}]`).forEach((el) => el.remove());

    const head = document.head;
    const add = (tag: string, attrs: Record<string, string>, text?: string) => {
      const el = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) {
        if (v) el.setAttribute(k, v);
      }
      if (text) el.textContent = text;
      el.setAttribute(MANAGED, '');
      head.appendChild(el);
    };

    add('meta', { name: 'description', content: seo.description });
    add('meta', { name: 'robots', content: seo.robots });
    if (seo.canonical) add('link', { rel: 'canonical', href: seo.canonical });

    add('meta', { property: 'og:title', content: seo.ogTitle });
    add('meta', { property: 'og:description', content: seo.ogDescription });
    add('meta', { property: 'og:type', content: seo.ogType });
    add('meta', { property: 'og:url', content: seo.canonical });
    if (seo.ogImage) add('meta', { property: 'og:image', content: seo.ogImage });
    if (extras.siteName) add('meta', { property: 'og:site_name', content: extras.siteName });

    add('meta', { name: 'twitter:card', content: seo.twitterCard });
    add('meta', { name: 'twitter:title', content: seo.ogTitle });
    add('meta', { name: 'twitter:description', content: seo.ogDescription });
    if (seo.ogImage) add('meta', { name: 'twitter:image', content: seo.ogImage });

    for (const block of seo.jsonLd ?? []) {
      add('script', { type: 'application/ld+json' }, JSON.stringify(block));
    }
  }, [seo, extras.siteName]);
}
